import * as THREE from 'three';
import { ROLES } from '../../shared/content/economy.js';
import { pickTable } from '../../shared/i18n.js';

// What the townspeople say: short lines shown as a speech bubble over the speaker for a few
// seconds, in the player's language (English, French or Cameroon Pidgin). A line is picked by the
// speaker's trade, the hour, the weather and whether the light is on, so the street talks about
// what is happening in it. Only people close enough to have a real body speak, and never more
// than a few at once.

const PCM = {
  reply: ['I dey fine, and you?', 'Fine o. How for you?', 'We dey, small small.', 'Na so we dey.', 'Welcome, massa.', 'Ashia, my broda.', 'How na? Long time!', 'You dey? We dey.'],
  general: ['Na so life be.', 'Country dey hard, but we dey.', 'Sun dey hot today o.', 'Make I reach house first.', 'This dust sef!', 'I go see you later.', 'Weekend don come!', 'Small time, I go come.', 'Make we go.', 'Wetin dey happen?'],
  rain: ['Rain wan fall, make we run!', 'This rain no go gree stop.', 'Water don enter my shoe.', 'Hold umbrella well!', 'Rain don spoil market today.'],
  night: ['Na late o, make I go house.', 'Where taxi dey for this night?', 'Mosquito plenty tonight.', 'I dey go sleep.', 'Make we go house before dark.'],
  cut: ['Light don go again!', 'This ENEO sef!', 'Na who get generator?', 'Light go, light come, na so.', 'Buy candle before night come.'],
  sunday: ['Make we go church.', 'Service don finish?', 'Na Sunday, rest small.', 'Pastor preach well today.'],
  morning: ['Good morning o!', 'You don chop?', 'Make I rush, work dey wait.', 'Traffic go plenty for Nkwen.'],
  trader: ['Tomato dey here, fresh one!', 'Come buy, customer!', 'Make I give you good price.', 'Na 500 for this one.', 'Pepper, pepper, sweet pepper!', 'Puff-puff dey hot!', 'Garri, groundnut, come!', 'Customer, come look am.'],
  rider: ['Benskin! Where you dey go?', 'Nkwen? Mile 4?', 'Na 300, no be 200.', 'Enter, I go carry you.', 'Fuel don cost o.', 'Hold well, we dey go.'],
  driver: ['Mile 4! Mile 4! Nkwen!', 'Up Station, one place!', 'Enter, enter, we dey go!', 'Commercial Avenue, 200!', 'Last person, we dey move!', 'Food market, one place!'],
  pupil: ['Teacher go flog me if I late.', 'Mama say make I buy bread.', 'Make we go school!', 'I never do my homework.'],
  student: ['Lecture start for eight.', 'Make we go campus.', 'I dey read for exam.', 'Who get the notes?'],
  farmer: ['Rain go help the corn.', 'I dey go farm.', 'Cocoyam don ready.', 'Goat don chop my plantain!'],
  teacher: ['Class, siddon!', 'Exam dey come.', 'Pikin dem no wan read.', 'Chalk don finish again.'],
  nurse: ['Hospital full today.', 'Take your drug well.', 'Night shift tire me.'],
  civil: ['Salary never come.', 'Office close for one.', 'Make I sign the paper.'],
  builder: ['Cement don cost o.', 'Bring the block!', 'We go finish roof today.'],
  tailor: ['Your dress go ready Friday.', 'Which style you want?', 'Ankara dey fine for you.'],
  mechanic: ['Your motor need new tyre.', 'Engine dey knock.', 'Come back for evening.'],
  hairdresser: ['Come make I plait your hair.', 'Braids, 5,000 only.', 'Siddon, I go start now.'],
  shopkeeper: ['Wetin you want buy?', 'Bread, sugar, milk dey.', 'No change, bring small money.', 'Shop dey open till night.'],
  herder: ['Cattle dey go Mile 4.', 'Grass don dry.', 'Mind the cow!'],
  tapper: ['Palm wine, fresh one!', 'Come drink mimbo.', 'Na from the top I bring am.'],
  pastor: ['God bless you, my son.', 'Come for service Sunday.', 'Praise the Lord!'],
  jobless: ['Any work dey?', 'I dey find work.', 'Make God help us.'],
  elder: ['My pikin, how you dey?', 'For our time, e no be so.', 'Greet your mama for me.', 'Siddon, make we talk.'],
  fon: ['The palace greet you.', 'Peace for the village.'],
  mayor: ['Council go fix the road.', 'We dey work for the town.'],
  fisher: ['Fish don finish for sea.', 'Fresh fish, come buy!', 'Sea rough today.'],
  police: ['Papers, please.', 'Move along, nothing dey here.', 'No park for here.'],
  passenger: 'Driver, I dey go {to}.',
};

const EN = {
  reply: ["I'm fine, and you?", 'Fine. How about you?', 'We are managing, little by little.', "That's how we are.", 'Welcome, sir.', 'Sorry, my brother.', 'How are you? Long time!', 'You well? We are well.'],
  general: ["That's life.", 'The country is hard, but we are here.', 'The sun is hot today.', 'Let me get home first.', 'This dust!', "I'll see you later.", 'The weekend is here!', "I'll be there in a moment.", "Let's go.", "What's happening?"],
  rain: ["Rain is coming, let's run!", 'This rain will not stop.', 'Water got into my shoe.', 'Hold the umbrella properly!', 'The rain spoiled the market today.'],
  night: ["It's late, let me go home.", 'Where is a taxi at this hour?', 'So many mosquitoes tonight.', "I'm going to sleep.", "Let's get home before dark."],
  cut: ['The light has gone again!', 'This ENEO!', 'Who has a generator?', 'Light goes, light comes, that is how it is.', 'Buy candles before night falls.'],
  sunday: ["Let's go to church.", 'Is the service over?', "It's Sunday, rest a little.", 'The pastor preached well today.'],
  morning: ['Good morning!', 'Have you eaten?', 'Let me rush, work is waiting.', 'Traffic will be heavy at Nkwen.'],
  trader: ['Tomatoes here, fresh ones!', 'Come and buy, customer!', "I'll give you a good price.", "It's 500 for this one.", 'Pepper, pepper, sweet pepper!', 'Hot puff-puff!', 'Garri, groundnuts, come!', 'Customer, come and look.'],
  rider: ['Bike! Where are you going?', 'Nkwen? Mile 4?', "It's 300, not 200.", "Get on, I'll take you.", 'Fuel is expensive now.', 'Hold on, here we go.'],
  driver: ['Mile 4! Mile 4! Nkwen!', 'Up Station, one seat!', 'Get in, get in, we are leaving!', 'Commercial Avenue, 200!', 'Last person, we are moving!', 'Food market, one seat!'],
  pupil: ["The teacher will cane me if I'm late.", 'Mama said to buy bread.', "Let's go to school!", "I haven't done my homework."],
  student: ['Lectures start at eight.', "Let's go to campus.", "I'm reading for the exam.", 'Who has the notes?'],
  farmer: ['The rain will help the maize.', "I'm going to the farm.", 'The cocoyams are ready.', 'A goat ate my plantain!'],
  teacher: ['Class, sit down!', 'Exams are coming.', "The children don't want to read.", 'The chalk is finished again.'],
  nurse: ['The hospital is full today.', 'Take your medicine properly.', 'The night shift wore me out.'],
  civil: ['The salary has not come.', 'The office closes at one.', 'Let me sign the paper.'],
  builder: ['Cement is expensive now.', 'Bring the blocks!', "We'll finish the roof today."],
  tailor: ['Your dress will be ready Friday.', 'Which style do you want?', 'Ankara suits you.'],
  mechanic: ['Your car needs a new tyre.', 'The engine is knocking.', 'Come back in the evening.'],
  hairdresser: ['Come, let me plait your hair.', 'Braids, only 5,000.', "Sit down, I'll start now."],
  shopkeeper: ['What do you want to buy?', 'We have bread, sugar, milk.', 'No change, bring small money.', 'The shop is open till night.'],
  herder: ['The cattle are going to Mile 4.', 'The grass has dried.', 'Mind the cow!'],
  tapper: ['Palm wine, fresh!', 'Come and drink.', 'I brought it from the top of the tree.'],
  pastor: ['God bless you, my son.', 'Come to the service on Sunday.', 'Praise the Lord!'],
  jobless: ['Is there any work?', "I'm looking for work.", 'May God help us.'],
  elder: ['My child, how are you?', 'In our time it was not like this.', 'Greet your mother for me.', "Sit down, let's talk."],
  fon: ['The palace greets you.', 'Peace to the village.'],
  mayor: ['The council will fix the road.', 'We are working for the town.'],
  fisher: ['The fish are gone from the sea.', 'Fresh fish, come and buy!', 'The sea is rough today.'],
  police: ['Papers, please.', 'Move along, nothing to see here.', 'No parking here.'],
  passenger: 'Driver, I am going to {to}.',
};

const FR = {
  reply: ['Ça va, et toi ?', 'Ça va. Et toi ?', 'On gère, petit à petit.', "C'est comme ça qu'on est.", 'Bienvenue, patron.', 'Courage, mon frère.', 'Comment ça va ? Ça fait longtemps !', 'Tu es là ? On est là.'],
  general: ["C'est la vie.", 'Le pays est dur, mais on est là.', "Le soleil tape aujourd'hui.", "Laisse-moi d'abord rentrer.", 'Cette poussière !', 'Je te vois plus tard.', 'Le week-end est arrivé !', "J'arrive tout de suite.", 'On y va.', "Qu'est-ce qui se passe ?"],
  rain: ['La pluie arrive, on court !', "Cette pluie ne va pas s'arrêter.", "L'eau est entrée dans ma chaussure.", 'Tiens bien le parapluie !', "La pluie a gâté le marché aujourd'hui."],
  night: ['Il est tard, je rentre.', 'Où trouver un taxi à cette heure ?', 'Trop de moustiques ce soir.', 'Je vais dormir.', 'Rentrons avant la nuit.'],
  cut: ['La lumière est encore partie !', 'Cette ENEO !', 'Qui a un groupe électrogène ?', "Lumière part, lumière revient, c'est comme ça.", 'Achète des bougies avant la nuit.'],
  sunday: ["Allons à l'église.", 'Le culte est fini ?', "C'est dimanche, repose-toi un peu.", "Le pasteur a bien prêché aujourd'hui."],
  morning: ['Bonjour !', 'Tu as mangé ?', 'Je me dépêche, le travail attend.', 'Il y aura des embouteillages à Nkwen.'],
  trader: ['Tomates ici, toutes fraîches !', 'Viens acheter, client !', 'Je te fais un bon prix.', "C'est 500 pour celui-là.", 'Piment, piment, piment doux !', 'Beignets tout chauds !', 'Garri, arachides, venez !', 'Client, viens voir.'],
  rider: ['Moto ! Tu vas où ?', 'Nkwen ? Mile 4 ?', "C'est 300, pas 200.", "Monte, je t'emmène.", "L'essence coûte cher maintenant.", 'Tiens-toi bien, on y va.'],
  driver: ['Mile 4 ! Mile 4 ! Nkwen !', 'Up Station, une place !', 'Montez, montez, on part !', 'Commercial Avenue, 200 !', 'Dernière personne, on bouge !', 'Food Market, une place !'],
  pupil: ['Le maître va me fouetter si je suis en retard.', "Maman a dit d'acheter du pain.", "Allons à l'école !", "Je n'ai pas fait mes devoirs."],
  student: ['Les cours commencent à huit heures.', 'Allons au campus.', "Je révise pour l'examen.", 'Qui a les notes ?'],
  farmer: ['La pluie va aider le maïs.', 'Je vais au champ.', 'Les macabos sont prêts.', 'Une chèvre a mangé mon plantain !'],
  teacher: ['Classe, asseyez-vous !', 'Les examens arrivent.', 'Les enfants ne veulent pas lire.', 'La craie est encore finie.'],
  nurse: ["L'hôpital est plein aujourd'hui.", 'Prends bien tes médicaments.', "La garde de nuit m'a épuisée."],
  civil: ["Le salaire n'est pas encore arrivé.", 'Le bureau ferme à treize heures.', 'Laisse-moi signer le papier.'],
  builder: ['Le ciment coûte cher maintenant.', 'Apportez les parpaings !', "On finit la toiture aujourd'hui."],
  tailor: ['Ta robe sera prête vendredi.', 'Quel modèle tu veux ?', 'Le pagne te va bien.'],
  mechanic: ["Ta voiture a besoin d'un pneu neuf.", 'Le moteur cogne.', 'Reviens ce soir.'],
  hairdresser: ['Viens, je te tresse.', 'Tresses, 5 000 seulement.', 'Assieds-toi, je commence.'],
  shopkeeper: ["Qu'est-ce que tu veux acheter ?", 'Il y a du pain, du sucre, du lait.', 'Pas de monnaie, apporte de la petite monnaie.', "La boutique est ouverte jusqu'au soir."],
  herder: ['Le bétail va à Mile 4.', "L'herbe a séché.", 'Attention à la vache !'],
  tapper: ['Vin de palme, tout frais !', 'Viens boire.', "Je l'ai descendu du sommet."],
  pastor: ['Que Dieu te bénisse, mon fils.', 'Viens au culte dimanche.', 'Louez le Seigneur !'],
  jobless: ['Il y a du travail ?', 'Je cherche du travail.', 'Que Dieu nous aide.'],
  elder: ['Mon enfant, comment vas-tu ?', "De notre temps, ce n'était pas comme ça.", 'Salue ta mère pour moi.', 'Assieds-toi, parlons.'],
  fon: ['Le palais te salue.', 'Paix au village.'],
  mayor: ['La commune va réparer la route.', 'On travaille pour la ville.'],
  fisher: ["Il n'y a plus de poisson en mer.", 'Poisson frais, venez acheter !', "La mer est agitée aujourd'hui."],
  police: ["Vos papiers, s'il vous plaît.", "Circulez, il n'y a rien à voir.", 'Stationnement interdit ici.'],
  passenger: 'Chauffeur, je vais à {to}.',
};

const BY_LANG = { en: EN, fr: FR, pcm: PCM };
const lines = () => pickTable(BY_LANG);

const MAX_ACTIVE = 5;
const REACH = 16; // metres from the player within which people speak

function bubble(text) {
  const pad = 16;
  const c = document.createElement('canvas');
  const g = c.getContext('2d');
  const font = '600 30px "Barlow Semi Condensed", Arial, sans-serif';
  g.font = font;
  const w = Math.ceil(g.measureText(text).width) + pad * 2;
  const h = 66;
  c.width = w;
  c.height = h;
  g.font = font;
  g.fillStyle = 'rgba(250,247,238,0.96)';
  g.beginPath();
  g.roundRect(0, 0, w, 48, 12);
  g.fill();
  // the tail
  g.beginPath();
  g.moveTo(w / 2 - 9, 47);
  g.lineTo(w / 2 + 9, 47);
  g.lineTo(w / 2, 62);
  g.closePath();
  g.fill();
  g.fillStyle = '#1b1e23';
  g.textBaseline = 'middle';
  g.fillText(text, pad, 25);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, fog: false, transparent: true }));
  sprite.renderOrder = 10;
  sprite.userData.aspect = w / h;
  return sprite;
}

export class Barks {
  constructor(scene) {
    this.scene = scene;
    this.active = []; // { who, sprite, until }
    this.timer = 2;
    this.on = true;
  }

  // The line a person would say now. ctx: { hour, weekday, rain, power, night }.
  pick(who, ctx) {
    const L = lines();
    const role = ROLES[who.role] ? ROLES[who.role].id : 'jobless';
    const pools = [];
    if (!ctx.power && ctx.night) pools.push(L.cut, L.cut);
    if (ctx.rain > 0.3) pools.push(L.rain, L.rain);
    if (ctx.night) pools.push(L.night);
    if (ctx.weekday === 0 && ctx.hour >= 7 && ctx.hour < 13) pools.push(L.sunday);
    if (ctx.hour >= 6 && ctx.hour < 9.5) pools.push(L.morning);
    if (L[role]) pools.push(L[role], L[role], L[role]);
    pools.push(L.general);
    const pool = pools[Math.floor(Math.random() * pools.length)];
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // What a passenger says getting in, in the player's language.
  passengerLine(to) {
    return lines().passenger.replace('{to}', to);
  }

  // Shows a line over someone for a few seconds (replacing what they were saying).
  say(who, text, seconds = 3.2) {
    this.stop(who);
    const sprite = bubble(text);
    this.scene.add(sprite);
    this.active.push({ who, sprite, until: performance.now() / 1000 + seconds });
    return text;
  }

  stop(who) {
    for (let i = this.active.length - 1; i >= 0; i--) {
      if (this.active[i].who !== who) continue;
      this.remove(i);
    }
  }

  remove(i) {
    const a = this.active[i];
    this.scene.remove(a.sprite);
    a.sprite.material.map.dispose();
    a.sprite.material.dispose();
    this.active.splice(i, 1);
  }

  // A greeting's answer.
  reply(who) {
    const L = lines();
    return this.say(who, L.reply[Math.floor(Math.random() * L.reply.length)]);
  }

  // Call every frame. people: the People; px, pz the player; ctx as for pick().
  update(dt, people, px, pz, camera, ctx) {
    const now = performance.now() / 1000;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const a = this.active[i];
      const w = a.who;
      const gone = w.x === undefined || now > a.until || !people.npcs.has(w.id) && !w.player;
      if (gone) {
        this.remove(i);
        continue;
      }
      a.sprite.position.set(w.x, (w.y !== undefined ? w.y : people.heightmap.heightAt(w.x, w.z)) + (w.age < 12 ? 1.5 : 2.15), w.z);
      const d = a.sprite.position.distanceTo(camera.position);
      const h = Math.max(0.45, Math.min(2.4, d * 0.05));
      a.sprite.scale.set(h * a.sprite.userData.aspect, h, 1);
    }
    if (!this.on) return;
    this.timer -= dt;
    if (this.timer > 0 || this.active.length >= MAX_ACTIVE) return;
    this.timer = 2.5 + Math.random() * 3.5;
    // someone close by, with a real body, who is not already talking
    const speaking = new Set(this.active.map((a) => a.who));
    const near = [];
    for (const n of people.npcs.values()) {
      if (!n.body || speaking.has(n)) continue;
      const d = Math.hypot(n.x - px, n.z - pz);
      if (d < REACH) near.push(n);
    }
    if (!near.length) return;
    const who = near[Math.floor(Math.random() * near.length)];
    this.say(who, this.pick(who, ctx));
  }
}
